import { Module } from 'vuex';
import { RootState } from '..';
import { RouteNames } from '@/utils/router/router.models';
import i18n from '@/i18n';
import { getFlatPublicRoutes } from '@/utils/router';
import { Location } from 'vue-router';
import { Summary, ConfigurationApi, Floor, FloorSummary } from '@/utils/api';
import { AxiosResponse } from 'axios';
import getFloorByID from '@/utils/helpers/getFloorByID';
import generateITSMedia from '@/utils/helpers/generateITSMedia';
import getFloorLayers from '@/utils/helpers/getFloorLayers';
import { FloorOverview } from '@/views/Summary/components/FloorOverview/FloorOverview.models';
import { ResponsiveImageDimensions } from '@/components/utils/ResponsiveImage/ResponsiveImage.models';
import { FloorGallerySlide } from '@/views/Summary/components/FloorGallery/FloorGallery.models';

const SET_INFO = 'SET_INFO';
const SET_ROUTES = 'SET_ROUTES';
const RESET_STATE = 'RESET_STATE';

export interface SummaryState {
  info?: Summary;
  routes: Location[];
}

const summaryInitialState = (): SummaryState => ({
  info: undefined,
  routes: []
});

const overviewMediaDimenstions: ResponsiveImageDimensions = {
  MQ1: [480, 480],
  MQ2: [768, 768],
  MQ3: [1024, 1024],
  MQ4: [1280, 1280],
  MQ5: [1440, 1440],
  MQ6: [1920, 1920]
};

const getFloorOverview = (key: string, floor: Floor, summary?: FloorSummary): FloorOverview => {
  const base = {
    ID: 'GF',
    alt: floor.name,
    media: generateITSMedia(floor.image, overviewMediaDimenstions)
  };
  const layers = getFloorLayers(floor, overviewMediaDimenstions);

  return {
    headline: i18n.t(`views.summary.floorsOverview.${key}.headline`) as string,
    highlightsDescription: i18n.t(`views.summary.floorsOverview.${key}.description`) as string,
    modulesDescription: i18n.t(`views.summary.config.modulesDescription`) as string,
    navName: i18n.t(`views.summary.floorsOverview.${key}.navName`) as string,
    navLabel: i18n.t(`views.summary.floorsOverview.${key}.cta`) as string,
    area: summary?.area,
    rooms: summary?.rooms,
    modules: summary?.modules,
    gallerySlides: [
      {
        base,
        layers
      },
      ...floor.zones.map(
        zone =>
          ({
            activeLayerID: zone.ID,
            transformation: summary?.zoneTransformations?.find(t => t.zoneID === zone.ID),
            base,
            layers
          } as FloorGallerySlide)
      )
    ]
  };
};

const configurationApi = new ConfigurationApi();

export const summaryModule: Module<SummaryState, RootState> = {
  namespaced: true,
  state: summaryInitialState(),
  getters: {
    groundFloor(state): Floor | undefined {
      return getFloorByID(state.info?.configuration.floors || [], 'GF');
    },
    groundFloorOverview(state, getters): FloorOverview | undefined {
      const groundFloor: Floor | undefined = getters.groundFloor;

      return groundFloor ? getFloorOverview('groundFloor', groundFloor, state.info?.summaryGroundFloor) : undefined;
    },

    upperFloor(state): Floor | undefined {
      return getFloorByID(state.info?.configuration.floors || [], 'UF');
    },
    upperFloorOverview(state, getters): FloorOverview | undefined {
      const upperFloor: Floor | undefined = getters.upperFloor;

      return upperFloor ? getFloorOverview('upperFloor', upperFloor, state.info?.summaryUpperFloor) : undefined;
    },

    atticEnabled(_state, getters): boolean {
      const attic = getters.attic as Floor | undefined;

      return !!attic?.zones[0].modules?.find(module => module.isSelected);
    },
    attic(state): Floor | undefined {
      return getFloorByID(state.info?.configuration.floors || [], 'ROF', 'ROZA');
    },
    atticOverview(state, getters): FloorOverview | undefined {
      const attic: Floor | undefined = getters.attic;

      return attic ? getFloorOverview('attic', attic, state.info?.summaryAttic) : undefined;
    }
  },
  actions: {
    async setInfo({ commit, rootState }) {
      if (rootState.configuration.houseConfiguration?.baufritzCode) {
        const summaryResponse: AxiosResponse<Summary> = await configurationApi.getSummary(
          rootState.configuration.houseConfiguration?.baufritzCode,
          rootState.app.locale
        );

        const info: Summary = summaryResponse.data;

        commit(SET_INFO, info);

        return info;
      }
    },
    updateRoutes({ commit, getters }) {
      const routes = getFlatPublicRoutes(i18n.locale)
        .filter(route => {
          const summaryRoutes = [
            RouteNames.Summary,
            RouteNames.SummaryFloorGroundFloor,
            RouteNames.SummaryFloorUpperFloor
          ];

          if (getters.atticEnabled) {
            summaryRoutes.push(RouteNames.SummaryFloorAttic);
          }

          return summaryRoutes.some(routeName => route.name?.slice(3) === routeName);
        })
        .map(route => ({
          name: route.name,
          path: route.path,
          type: route.meta.type
        }));

      commit(SET_ROUTES, routes);
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    }
  },
  mutations: {
    [SET_INFO](state, info: Summary) {
      state.info = info;
    },
    [SET_ROUTES](state, routes: Location[]) {
      state.routes = routes;
    },
    [RESET_STATE](state) {
      Object.assign(state, summaryInitialState());
    }
  }
};
